import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * @typedef {Object} StickyPurchaseRefs
 * @property {HTMLElement} stickyBar
 * @property {HTMLElement} totalValue
 * @property {HTMLButtonElement} addToCartButton
 * @property {HTMLElement} buttonLabel
 */

/**
 * A fixed purchase proxy that appears only after the real PDP purchase CTA has
 * completely left the viewport. The original CTA remains the sole commerce
 * authority; this component mirrors its presentation and delegates one click.
 *
 * @extends {Component<StickyPurchaseRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'totalValue', 'addToCartButton', 'buttonLabel'];

  /** @type {IntersectionObserver | null} */
  #purchaseIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #footerIntersectionObserver = null;

  /** @type {ResizeObserver | null} */
  #stickyBarResizeObserver = null;

  /** @type {MutationObserver | null} */
  #sourceMutationObserver = null;

  /** @type {MutationObserver | null} */
  #disclosureMutationObserver = null;

  /** @type {MutationObserver | null} */
  #surfaceMutationObserver = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #sourceButton = null;

  /** @type {HTMLElement | null} */
  #sourceTotal = null;

  /** @type {HTMLElement | null} */
  #footer = null;

  /** @type {boolean} */
  #sourceHasPassedViewport = false;

  /** @type {boolean} */
  #footerIsNear = false;

  /** @type {number} */
  #footerGuardHeight = -1;

  /** @type {boolean} */
  #isVisible = false;

  /** @type {boolean} */
  #isDelegatingClick = false;

  /** @type {number | null} */
  #reconnectFrame = null;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const section = this.closest('.shopify-section');

    section?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleExternalStateChange, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleExternalStateChange, { signal });

    this.#observeDisclosures(section);
    this.#observeBlockingSurfaces();
    this.#connectFooterGuard();
    this.#connectPurchaseSource();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#purchaseIntersectionObserver?.disconnect();
    this.#footerIntersectionObserver?.disconnect();
    this.#stickyBarResizeObserver?.disconnect();
    this.#sourceMutationObserver?.disconnect();
    this.#disclosureMutationObserver?.disconnect();
    this.#surfaceMutationObserver?.disconnect();
    this.#abortController.abort();

    if (this.#reconnectFrame !== null) {
      cancelAnimationFrame(this.#reconnectFrame);
    }
  }

  /**
   * Delegates one activation to the real purchase control.
   */
  handleAddToCartClick = () => {
    if (
      !this.#isVisible ||
      this.#isDelegatingClick ||
      !this.#sourceButton ||
      this.#sourceButton.disabled ||
      this.refs.addToCartButton.disabled
    ) {
      return;
    }

    this.#isDelegatingClick = true;
    this.refs.addToCartButton.disabled = true;
    this.refs.addToCartButton.tabIndex = -1;

    this.#sourceButton.click();

    queueMicrotask(() => {
      this.#isDelegatingClick = false;
      this.#syncFromPurchaseSource();
    });
  };

  /**
   * Locates the actual approved PDP purchase action and its authoritative total.
   */
  #connectPurchaseSource() {
    const section = this.closest('.shopify-section');
    if (!section) return;

    this.#purchaseIntersectionObserver?.disconnect();
    this.#sourceMutationObserver?.disconnect();

    const addonButton = section.querySelector('.product-addons .addon-add-all-btn');
    const productForm = this.#getProductForm();
    const nativeButton = productForm?.querySelector('[ref="addToCartButton"]');

    this.#sourceButton =
      addonButton instanceof HTMLButtonElement
        ? addonButton
        : nativeButton instanceof HTMLButtonElement
          ? nativeButton
          : null;

    const addonTotal = section.querySelector('.product-addons .addon-total__price');
    const nativeTotal = section.querySelector('product-price [ref="priceContainer"] .price');
    this.#sourceTotal =
      addonTotal instanceof HTMLElement
        ? addonTotal
        : nativeTotal instanceof HTMLElement
          ? nativeTotal
          : null;

    if (!this.#sourceButton) {
      this.#sourceHasPassedViewport = false;
      this.#setVisibility(false);
      return;
    }

    this.#sourceMutationObserver = new MutationObserver(() => {
      this.#syncFromPurchaseSource();
    });
    this.#sourceMutationObserver.observe(this.#sourceButton, {
      attributes: true,
      attributeFilter: ['aria-busy', 'aria-label', 'class', 'data-added', 'disabled'],
      childList: true,
      characterData: true,
      subtree: true,
    });

    if (this.#sourceTotal && this.#sourceTotal !== this.#sourceButton) {
      this.#sourceMutationObserver.observe(this.#sourceTotal, {
        childList: true,
        characterData: true,
        subtree: true,
      });
    }

    this.#purchaseIntersectionObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;

        const hasVisibleIntersection = entry.isIntersecting && entry.intersectionRatio > 0;
        this.#sourceHasPassedViewport =
          !hasVisibleIntersection && entry.boundingClientRect.bottom <= 0;
        this.#updateVisibility();
      },
      { threshold: 0 }
    );
    this.#purchaseIntersectionObserver.observe(this.#sourceButton);

    this.#measurePurchaseSource();
    this.#syncFromPurchaseSource();
  }

  /**
   * Provides a synchronous initial visibility state while IntersectionObserver
   * waits for its first delivery.
   */
  #measurePurchaseSource() {
    if (!this.#sourceButton || this.#sourceButton.getClientRects().length === 0) {
      this.#sourceHasPassedViewport = false;
      return;
    }

    const rect = this.#sourceButton.getBoundingClientRect();
    const intersectsViewport = rect.bottom > 0 && rect.top < window.innerHeight;
    this.#sourceHasPassedViewport = !intersectsViewport && rect.bottom <= 0;
  }

  /**
   * Mirrors only visible presentation state. No product or cart calculation is
   * performed here.
   */
  #syncFromPurchaseSource() {
    if (!this.#sourceButton) {
      this.#setVisibility(false);
      return;
    }

    const sourceLabel =
      this.#sourceButton.querySelector('.add-to-cart-text__content') ?? this.#sourceButton;
    const label = this.#normalizeText(sourceLabel.textContent);
    const accessibleLabel =
      this.#sourceButton.getAttribute('aria-label') || label;

    if (label && this.refs.buttonLabel.textContent !== label) {
      this.refs.buttonLabel.textContent = label;
    }

    if (accessibleLabel) {
      this.refs.addToCartButton.setAttribute('aria-label', accessibleLabel);
    }

    const isDisabled = this.#sourceButton.disabled || this.#isDelegatingClick;
    this.refs.addToCartButton.disabled = isDisabled;

    const isBusy = this.#sourceButton.getAttribute('aria-busy') === 'true';
    if (isBusy) {
      this.refs.addToCartButton.setAttribute('aria-busy', 'true');
    } else {
      this.refs.addToCartButton.removeAttribute('aria-busy');
    }

    const total = this.#normalizeText(this.#sourceTotal?.textContent);
    if (total && this.refs.totalValue.textContent !== total) {
      this.refs.totalValue.textContent = total;
    }

    this.refs.addToCartButton.tabIndex =
      this.#isVisible && !isDisabled ? 0 : -1;
    this.#updateVisibility();
  }

  /**
   * Hides the bar before the footer reaches the viewport area the fixed bar
   * would cover. The measured bar height includes responsive and safe-area
   * padding, and the observer is rebuilt only when that height changes.
   */
  #connectFooterGuard() {
    const footer = document.querySelector('body > footer') ?? document.querySelector('footer');
    this.#footer = footer instanceof HTMLElement ? footer : null;
    this.#footerIsNear = false;

    if (!this.#footer) return;

    if ('ResizeObserver' in window) {
      this.#stickyBarResizeObserver = new ResizeObserver(() => {
        this.#refreshFooterObserver();
      });
      this.#stickyBarResizeObserver.observe(this.refs.stickyBar);
    }

    this.#refreshFooterObserver();
  }

  #refreshFooterObserver() {
    if (!this.#footer) return;

    const barHeight = Math.ceil(this.refs.stickyBar.getBoundingClientRect().height);
    if (this.#footerIntersectionObserver && barHeight === this.#footerGuardHeight) return;

    this.#footerGuardHeight = barHeight;
    this.#footerIntersectionObserver?.disconnect();

    const footerRect = this.#footer.getBoundingClientRect();
    this.#footerIsNear =
      footerRect.bottom > 0 && footerRect.top <= window.innerHeight + barHeight;
    this.#updateVisibility();

    this.#footerIntersectionObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;

        const footerIsNear = entry.isIntersecting;
        if (footerIsNear === this.#footerIsNear) return;

        this.#footerIsNear = footerIsNear;
        this.#updateVisibility();
      },
      {
        rootMargin: `0px 0px ${barHeight}px 0px`,
        threshold: 0,
      }
    );
    this.#footerIntersectionObserver.observe(this.#footer);
  }

  /**
   * Observes disclosure open/close state without changing configurator logic.
   * @param {Element | null} section
   */
  #observeDisclosures(section) {
    if (!section) return;

    this.#disclosureMutationObserver = new MutationObserver(() => {
      this.#updateVisibility();
    });
    this.#disclosureMutationObserver.observe(section, {
      attributes: true,
      attributeFilter: ['aria-expanded'],
      subtree: true,
    });
  }

  /**
   * Keeps the bar inert beneath cart drawers, lightboxes and other dialogs.
   */
  #observeBlockingSurfaces() {
    this.#surfaceMutationObserver = new MutationObserver(() => {
      this.#updateVisibility();
    });
    this.#surfaceMutationObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['open'],
      childList: true,
      subtree: true,
    });
  }

  #hasOpenDisclosure() {
    const section = this.closest('.shopify-section');
    return Boolean(section?.querySelector('.addon-choice-trigger[aria-expanded="true"]'));
  }

  #hasBlockingSurface() {
    return Boolean(document.querySelector('dialog[open]'));
  }

  #updateVisibility() {
    const shouldShow =
      this.dataset.variantAvailable === 'true' &&
      this.#sourceHasPassedViewport &&
      !this.#footerIsNear &&
      !this.#hasOpenDisclosure() &&
      !this.#hasBlockingSurface();

    this.#setVisibility(shouldShow);
  }

  /**
   * Applies the complete hidden/interactive invariant.
   * @param {boolean} shouldShow
   */
  #setVisibility(shouldShow) {
    if (this.#isVisible === shouldShow) {
      this.refs.addToCartButton.tabIndex =
        shouldShow && !this.refs.addToCartButton.disabled ? 0 : -1;
      return;
    }

    this.#isVisible = shouldShow;
    this.refs.stickyBar.dataset.stuck = String(shouldShow);
    this.refs.stickyBar.setAttribute('aria-hidden', String(!shouldShow));
    this.refs.stickyBar.toggleAttribute('inert', !shouldShow);

    if (!shouldShow && this.refs.stickyBar.contains(document.activeElement)) {
      this.refs.addToCartButton.blur();
    }

    this.refs.addToCartButton.tabIndex =
      shouldShow && !this.refs.addToCartButton.disabled ? 0 : -1;
  }

  /**
   * Reconnects after the theme replaces variant-dependent purchase markup.
   * @param {CustomEvent} event
   */
  #handleVariantUpdate = (event) => {
    if (String(event.detail?.data?.productId) !== this.dataset.productId) return;

    const variant = event.detail?.resource;
    const replacement = event.detail?.data?.html?.querySelector('sticky-add-to-cart');

    if (replacement) {
      this.dataset.variantAvailable = replacement.dataset.variantAvailable || 'false';
    } else if (variant && typeof variant.available === 'boolean') {
      this.dataset.variantAvailable = String(variant.available);
    }

    if (variant?.id) {
      this.dataset.currentVariantId = String(variant.id);
    }

    this.#scheduleSourceReconnect();
  };

  #handleExternalStateChange = () => {
    this.#syncFromPurchaseSource();
    this.#updateVisibility();
  };

  #scheduleSourceReconnect() {
    if (this.#reconnectFrame !== null) {
      cancelAnimationFrame(this.#reconnectFrame);
    }

    this.#reconnectFrame = requestAnimationFrame(() => {
      this.#reconnectFrame = null;
      this.#connectPurchaseSource();
    });
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    const section = this.closest('.shopify-section');
    if (!productId || !section?.id) return null;

    return document.querySelector(
      `#${CSS.escape(section.id)} product-form-component[data-product-id="${CSS.escape(productId)}"]`
    );
  }

  /**
   * @param {string | null | undefined} value
   */
  #normalizeText(value) {
    return (value || '').replace(/\s+/g, ' ').trim();
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
